import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Radio,
  Alert,
  IconButton,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  ENTERPRISE_ADDINS_CART_DETAILS,
  EG_GET_CART_DETAILS,
  EG_ADD_TO_CART,
  EG_REMOVE_FROM_CART,
} from "../../../constants/api-constants";
import ApiServices from "../../../services/ApiServices";
import { useLoading } from "../../../hooks/useLoader";
import { toast } from "react-toastify";
import "./AddInsSubscriptionAddToCart.css";
import { entityType } from "../../../constants/common";
import { SUBSCRIPTION_STATUS } from "../constants";
import {
  isSuperAdminOrAdminRole,
  isEnterpriseUserRole,
} from "../../../utilities/UserRoleUtils";
import Confirmation from "../../../common/Confirmation/Confirmation";
import getConfirmAccessTitleText from "./getConfirmAccessTitleText";
import AccessGrantWithDate from "./AccessGrantWithDate";

/**
 * AddInsSubscriptionAddToCart Component
 * Modal for displaying Add-in details and subscription options
 *
 * Props:
 * - open: boolean - Controls modal visibility
 * - enterpriseId: number - Enterprise ID to fetch cart details
 * - addInsId: number - Add-in ID to fetch details
 * - onClose: function - Callback when modal closes
 * - onAddToCart: function - Callback when add to cart is clicked
 * - onGoToCart: function - Callback when go to cart is clicked
 * - alreadyInCart: boolean - Flag to show Update Cart button instead
 * - cartItemId: number - ID of existing cart item (for update)
 */
const AddInsSubscriptionAddToCart = ({
  open,
  enterpriseId,
  addInsId,
  onClose,
  onAddToCart,
  onRemoveFromCart,
  onGoToCart,
  onUpdateAccess,
  alreadyInCart = false,
  cartItemId = null,
  cartCount = 0,
}) => {
  const { showLoader } = useLoading();
  const canManageCart = isEnterpriseUserRole();
  const canManageAccess = isSuperAdminOrAdminRole();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addInDetails, setAddInDetails] = useState(null);
  const [isInCart, setIsInCart] = useState(alreadyInCart);
  const [isFreeAlreadyInCart, setIsFreeAlreadyInCart] = useState(false);
  const [activeCartItemId, setActiveCartItemId] = useState(cartItemId);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [initialSubscription, setInitialSubscription] = useState(null);
  const [cartButtonDisabled, setCartButtonDisabled] = useState(!alreadyInCart);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showRevokeAccessConfirmation, setShowRevokeAccessConfirmation] =
    useState(false);
  const [showGrantAccessPopup, setShowGrantAccessPopup] = useState(false);

  // Fetch add-in details on modal open
  useEffect(() => {
    if (open && enterpriseId && addInsId) {
      fetchAddInDetails();
    }
  }, [open, enterpriseId, addInsId]);

  // Check if subscription selection has changed to enable/disable update button
  useEffect(() => {
    if (isInCart) {
      const hasChanged =
        selectedSubscription?.addInSubscriptionId !==
        initialSubscription?.addInSubscriptionId;
      setCartButtonDisabled(!hasChanged);
    } else {
      // For new items, disable button if no selection made
      setCartButtonDisabled(!selectedSubscription);
    }
  }, [selectedSubscription, initialSubscription, isInCart]);

  useEffect(() => {
    setIsInCart(alreadyInCart);
    setActiveCartItemId(cartItemId);
    setIsFreeAlreadyInCart(false);
  }, [alreadyInCart, cartItemId]);

  const fetchCartDetails = async (
    subscriptions = [],
    currentAddInId = addInsId,
    currentCostType = addInDetails?.costType,
  ) => {
    try {
      const url = EG_GET_CART_DETAILS("USD");
      const cartResponse = await ApiServices.httpGet(url);

      const items = cartResponse?.result?.items || [];
      const matchingCartItem = items.find(
        (item) => Number(item?.itemId) === Number(currentAddInId),
      );

      if (matchingCartItem) {
        setIsInCart(true);
        setActiveCartItemId(
          matchingCartItem?.cartItemId ?? matchingCartItem?.id ?? null,
        );

        const isFreeCartMatch =
          Number(currentCostType) === 1 &&
          Number(matchingCartItem?.frequency) === 99;
        setIsFreeAlreadyInCart(isFreeCartMatch);

        if (subscriptions?.length > 0) {
          const matchedSubscription =
            subscriptions.find(
              (subscription) =>
                Number(subscription?.frequency) ===
                Number(matchingCartItem?.frequency),
            ) || subscriptions[0];
          setSelectedSubscription(matchedSubscription);
          setInitialSubscription(matchedSubscription);
        }
      } else {
        setIsInCart(false);
        setIsFreeAlreadyInCart(false);
        setActiveCartItemId(null);
        setSelectedSubscription(null);
        setInitialSubscription(null);
      }

      return matchingCartItem;
    } catch (err) {
      console.error("Error fetching cart details:", err);
      return null;
    }
  };

  const fetchAddInDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      showLoader(true);

      const url = ENTERPRISE_ADDINS_CART_DETAILS(enterpriseId, addInsId);
      let response = await ApiServices.httpGet(url);
      //response = mockAddInsDetails

      if (response?.responseStatus?.isSuccess && response?.result) {
        const details = response.result;
        setAddInDetails(details);
        setSubscriptionStatus(details.subscriptionStatus);
        await fetchCartDetails(
          details.availableSubscriptions || [],
          details.addInId,
          details.costType,
        );

        showLoader(false);
      } else {
        setError(
          response?.responseStatus?.error || "Failed to load add-in details",
        );
        showLoader(false);
      }
    } catch (err) {
      console.error("Error fetching add-in details:", err);
      setError(err.message || "Failed to load add-in details");
      toast.error("Failed to load add-in details");
      showLoader(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionSelect = (subscription) => {
    if (canManageAccess) {
      return;
    }
    setSelectedSubscription(subscription);
  };

  const handleAddToCart = async () => {
    const isPaid = addInDetails?.costType === 2;
    const defaultSubscription =
      addInDetails?.availableSubscriptions?.[0] || null;
    const effectiveSubscription = isPaid
      ? selectedSubscription
      : selectedSubscription || defaultSubscription;

    // Validate for paid add-ins
    if (isPaid && !effectiveSubscription) {
      toast.warning("Please select a subscription plan");
      return;
    }

    if (!addInDetails?.addInId) {
      toast.error("Invalid add-in details");
      return;
    }

    const payload = {
      itemId: addInDetails?.addInId,
      quantity: 1,
      transactionType: 5,
      frequency:
        addInDetails?.costType === 1
          ? 99
          : effectiveSubscription?.frequency || 1,
      currency: "USD",
      entityType: entityType.ENTERPRISES_GROUP,
    };

    try {
      setLoading(true);
      const response = await ApiServices.httpPost(EG_ADD_TO_CART, payload);
      if (response?.responseStatus?.isSuccess) {
        await fetchCartDetails();
      }
    } catch (err) {
      console.error("Error adding item to cart:", err);
      toast.error("Failed to add item to cart");
      return;
    } finally {
      setLoading(false);
    }

    if (onAddToCart) {
      onAddToCart({
        addInId: addInDetails?.addInId,
        addInSubscriptionId: effectiveSubscription?.addInSubscriptionId,
        cartItemId: isInCart ? activeCartItemId : null,
      });
    }
  };

  const handleRemoveFromCart = async () => {
    try {
      setLoading(true);
      const payload = {
        transactionType: 5,
        entityType: entityType.ENTERPRISES_GROUP,
      };
      await ApiServices.httpDelete(
        EG_REMOVE_FROM_CART(addInDetails?.addInId),
        payload,
      );
      setIsInCart(false);
      setIsFreeAlreadyInCart(false);
      setActiveCartItemId(null);
      await fetchCartDetails(
        addInDetails?.availableSubscriptions || [],
        addInDetails?.addInId,
        addInDetails?.costType,
      );
      toast.success("Removed from cart");
      if (onRemoveFromCart) {
        onRemoveFromCart();
      }
      handleClose();
    } catch (err) {
      console.error("Error removing item from cart:", err);
      toast.error("Failed to remove item from cart");
    } finally {
      setLoading(false);
    }
  };

  const handleGoToCart = () => {
    if (onGoToCart) {
      onGoToCart();
    }
  };

  const handleAccessChange = () => {
    if (addInDetails?.hasAdminAccess) {
      setShowRevokeAccessConfirmation(true);
      return;
    }
    setShowGrantAccessPopup(true);
  };

  const handleConfirmRevokeAccessChange = async (_event, reason) => {
    if (!onUpdateAccess || !addInDetails?.addInId) {
      setShowRevokeAccessConfirmation(false);
      return;
    }

    const isUpdated = await onUpdateAccess({
      addInId: addInDetails.addInId,
      hasAccess: false,
      reason: reason || null,
    });

    setShowRevokeAccessConfirmation(false);
    if (isUpdated) {
      handleClose();
    }
  };

  const handleConfirmGrantAccessChange = async ({
    effectiveFromDate,
    effectiveToDate,
  }) => {
    if (!onUpdateAccess || !addInDetails?.addInId) {
      setShowGrantAccessPopup(false);
      return;
    }

    const isUpdated = await onUpdateAccess({
      addInId: addInDetails.addInId,
      hasAccess: true,
      effectiveFromDate,
      effectiveToDate,
      reason: null,
    });

    setShowGrantAccessPopup(false);
    if (isUpdated) {
      handleClose();
    }
  };

  const handleClose = () => {
    // Reset state
    setSelectedSubscription(null);
    setInitialSubscription(null);
    setError(null);
    setAddInDetails(null);
    onClose();
  };

  const getFrequencyLabel = (frequency) => {
    const frequencyMap = {
      1: "Monthly",
      2: "Quarterly",
      3: "Semi-Annual",
      4: "Annual",
      99: "One Time Cost",
    };
    return frequencyMap[frequency] || "Unknown";
  };

  const formatPrice = (price) => {
    return `$${Number(price).toFixed(2)}`;
  };

  const isPaidAddIn = addInDetails?.costType === 2;
  const isFreeAddIn = addInDetails?.costType === 1;

  const renderModalContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          Loading add-in details...
        </Box>
      );
    }

    if (!addInDetails) {
      return null;
    }

    return (
      <Box>
        {/* Add-in Details Gray Box */}
        <Box className="addins-details-box">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Add-in Name
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "700 !important" }}
                >
                  {addInDetails?.name}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Vendor
                </Typography>
                <Typography variant="body2">
                  {addInDetails?.vendorName}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  TYPE
                </Typography>
                <Typography variant="body2">
                  {addInDetails?.addInTypeText}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Applicable To
                </Typography>
                <Typography variant="body2">
                  Enterprise Group, Subscriber
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Cost Type
                </Typography>
                <Typography variant="body2">
                  {isPaidAddIn ? "Paid" : "Free"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Description
                </Typography>
                <Typography variant="body2">
                  {addInDetails?.addInDescription || "N/A"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Subscription Options Table - Only for Paid Add-ins and non-admin users */}
        {isPaidAddIn && !canManageAccess && addInDetails?.availableSubscriptions?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 0,
                pl: 1,
                fontWeight: 400,
                fontSize: "16px",
                color: "rgba(91, 94, 112, 1)",
              }}
            >
              Subscription Options
            </Typography>

            <TableContainer component="div">
              <Table>
                <TableHead className="eg-subscription-tablehead">
                  <TableRow className="table-header-row">
                    <TableCell align="center" sx={{ fontWeight: 400 }}>
                      Select
                    </TableCell>
                    <TableCell sx={{ fontWeight: 400 }}>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 400 }}>Frequency</TableCell>
                    <TableCell sx={{ fontWeight: 400 }}>Auto Renewal</TableCell>
                    <TableCell sx={{ fontWeight: 400 }}>Trial</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addInDetails.availableSubscriptions.map((subscription) => (
                    <TableRow
                      key={subscription.addInSubscriptionId}
                      hover
                      sx={{
                        backgroundColor:
                          selectedSubscription?.addInSubscriptionId ===
                          subscription.addInSubscriptionId
                            ? "#f0f0f0"
                            : "transparent",
                        cursor: canManageAccess ? "default" : "pointer",
                      }}
                      onClick={
                        canManageAccess
                          ? undefined
                          : () => handleSubscriptionSelect(subscription)
                      }
                      className="addin-tbl-row"
                    >
                      <TableCell align="center">
                        <Radio
                          disabled={
                            subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBED ||
                            canManageAccess
                          }
                          checked={
                            selectedSubscription?.addInSubscriptionId ===
                            subscription.addInSubscriptionId
                          }
                          onChange={() =>
                            handleSubscriptionSelect(subscription)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {formatPrice(subscription.actualValue)}
                      </TableCell>
                      <TableCell>
                        {getFrequencyLabel(subscription.frequency)}
                      </TableCell>
                      <TableCell>
                        {subscription.isRecurring ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>
                        {subscription.enableTrial
                          ? `${subscription.trialDurationDays} days`
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Validation Message for Paid Add-ins */}
        {isPaidAddIn &&
          !selectedSubscription &&
          !canManageAccess &&
          SUBSCRIPTION_STATUS.SUBSCRIBED !== subscriptionStatus && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Please select a subscription plan to proceed.
            </Alert>
          )}

        {/* Info Message for Free Add-ins already in cart */}
        {isFreeAlreadyInCart && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This free add-in is already added in cart.
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="addins-cart-modal"
    >
      <DialogTitle
        className="modal-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          component="span"
          className="modal-title-typography"
        >
          Add-in Details
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: "#666",
            p: 0.5,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="modal-content">
        {error && (
          <Alert severity="error" sx={{ mb: 0, mt: 2 }}>
            {error}
          </Alert>
        )}
        {renderModalContent()}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
        {canManageAccess && (
          <Button
            onClick={handleAccessChange}
            variant="contained"
            color={addInDetails?.hasAdminAccess ? "error" : "primary"}
            disabled={loading || !addInDetails?.addInId}
          >
            {addInDetails?.hasAdminAccess ? "Revoke Access" : "Grant Access"}
          </Button>
        )}
        {canManageCart && (
          <>
            {subscriptionStatus !== SUBSCRIPTION_STATUS.SUBSCRIBED && (
              <>
                {isFreeAlreadyInCart ? (
                  <Button
                    onClick={handleRemoveFromCart}
                    variant="contained"
                    color="error"
                    disabled={loading}
                  >
                    Remove from Cart
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={handleAddToCart}
                      variant="contained"
                      color="primary"
                      disabled={!isFreeAddIn && (cartButtonDisabled || loading)}
                    >
                      {isInCart ? "Update Cart" : "Add to Cart"}
                    </Button>
                    {isInCart && (
                      <Button
                        onClick={handleRemoveFromCart}
                        variant="contained"
                        color="error"
                        disabled={loading}
                      >
                        Remove from Cart
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
            <Button
              onClick={handleGoToCart}
              variant="contained"
              startIcon={
                <Badge
                  badgeContent={cartCount}
                  showZero
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "rgba(255, 60, 63, 1)",
                      color: "#fff",
                    },
                  }}
                  overlap="circular"
                >
                  <ShoppingCartOutlinedIcon style={{ fontSize: "20px" }} />
                </Badge>
              }
            >
              Go to Cart
            </Button>
          </>
        )}
        <Button onClick={onClose} variant="contained">
          Return to List
        </Button>
      </DialogActions>

      {/* Access Change Confirmation Dialog */}

      <Confirmation
        open={showRevokeAccessConfirmation}
        handleClose={() => setShowRevokeAccessConfirmation(false)}
        title={getConfirmAccessTitleText(true)}
        showReason
        onCancel={() => setShowRevokeAccessConfirmation(false)}
        onConfirm={handleConfirmRevokeAccessChange}
        cancelButtonLabel="No"
        confirmButtonLabel="Yes"
      />

      <AccessGrantWithDate
        open={showGrantAccessPopup}
        handleClose={() => setShowGrantAccessPopup(false)}
        title={getConfirmAccessTitleText(false)}
        onCancel={() => setShowGrantAccessPopup(false)}
        onConfirm={handleConfirmGrantAccessChange}
        cancelButtonLabel="No"
        confirmButtonLabel="Yes"
      />
    </Dialog>
  );
};

AddInsSubscriptionAddToCart.propTypes = {
  open: PropTypes.bool.isRequired,
  enterpriseId: PropTypes.number.isRequired,
  addInsId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func.isRequired,
  onRemoveFromCart: PropTypes.func,
  onGoToCart: PropTypes.func.isRequired,
  onUpdateAccess: PropTypes.func,
  alreadyInCart: PropTypes.bool,
  cartItemId: PropTypes.number,
  cartCount: PropTypes.number,
};

AddInsSubscriptionAddToCart.defaultProps = {
  alreadyInCart: false,
  cartItemId: null,
  cartCount: 0,
  onRemoveFromCart: null,
  onUpdateAccess: null,
};

export default AddInsSubscriptionAddToCart;
